import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Label } from 'ui/Label';
import dayjs from 'dayjs';
import { StyledApplicationContractForm } from './style';
import { DateProgress } from '../../../DateProgress';
import { Listing, FileMeta } from '../../../../types/ListingData';
import { Divider } from 'ui/Divider';
import { CustomDocument } from 'ui/Upload/Uploader';
import { StyledCustomUploadList } from 'ui/Upload/Uploader/style';
import { UploadFile } from 'antd';

interface NewApplicationContractFormProps {
  listingData?: Listing | null;
  isEditing?: boolean;
}

export const NewApplicationContractForm = ({
  listingData,
  isEditing = false,
}: NewApplicationContractFormProps) => {
  const intl = useIntl();

  const contractFiles = listingData?.contract_terms?.files || [];

  const handleDownloadContractTemplate = useCallback(
    (fileId?: number) => {
      if (listingData?.id) {
        const downloadUrl = fileId
          ? `${window.runConfig?.backendUrl}/api/v2/listings/${listingData.id}/contract-terms/download?file_id=${fileId}`
          : `${window.runConfig?.backendUrl}/api/v2/listings/${listingData.id}/contract-terms/download`;
        window.open(downloadUrl, '_blank');
      }
    },
    [listingData?.id]
  );

  const filesForDisplay: UploadFile[] = contractFiles.map((file: FileMeta) => {
    const name = file.original_name || file.name || '';
    const extFromName = name?.includes('.') ? name.split('.').pop() : undefined;
    const displayFormat = (extFromName || file.mime_type || '')
      .toString()
      .toUpperCase();
    const displaySize = file.size_bytes || 0;

    return {
      uid: String(file.id),
      name: name,
      status: 'done' as const,
      file_size: displaySize,
      file_format: displayFormat,
      created_at: file.created_at,
      fileId: file.id,
    } as UploadFile;
  });

  return (
    <StyledApplicationContractForm>
      {/* Contract Period */}
      <div className="contract-period">
        <div className="contract-period-date">
          <Label
            label={
              intl.formatMessage({
                id: 'listings.application.contract_start_date',
              }) + ':'
            }
          />
          <div className="deadline-date">
            {dayjs(listingData?.contract_terms?.contract_start_date).format(
              'DD.MM.YYYY'
            )}
          </div>
        </div>
        {listingData?.contract_terms?.contract_start_date &&
          listingData?.contract_terms?.contract_end_date && (
            <DateProgress
              startDate={listingData.contract_terms.contract_start_date}
              endDate={listingData.contract_terms.contract_end_date}
            />
          )}
        <div className="contract-period-date">
          <Label
            label={
              intl.formatMessage({
                id: 'listings.application.contract_end_date',
              }) + ':'
            }
          />
          <div className="deadline-date">
            {dayjs(listingData?.contract_terms?.contract_end_date).format(
              'DD.MM.YYYY'
            )}
          </div>
        </div>
      </div>

      <Divider />

      {/* Payment Terms */}
      <div className="terms">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.payment_terms',
          })}
          title={true}
        />
        <div className="contract-text-value">
          {listingData?.contract_terms?.payment_conditions || '---'}
        </div>
      </div>

      <Divider />

      {/* Additional Contract Terms */}
      <div className="terms">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.additional_contract_terms',
          })}
          title={true}
        />
        <div className="contract-text-value">
          {listingData?.contract_terms?.additional_contract_terms || '---'}
        </div>
      </div>

      {/* Contract Template */}
      {filesForDisplay.length > 0 && (
        <>
          <Divider />
          <Label
            label={intl.formatMessage({
              id: 'listings.application.contract_template',
            })}
            title={true}
          />
          <StyledCustomUploadList>
            {filesForDisplay.map((file) => (
              <CustomDocument
                key={file.uid}
                file={file}
                onDownload={() =>
                  handleDownloadContractTemplate((file as any).fileId)
                }
                showActions={true}
              />
            ))}
          </StyledCustomUploadList>
        </>
      )}
    </StyledApplicationContractForm>
  );
};
